import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { api, type BriefDetail } from '../lib/api'
import { Input } from '../components/ui/Input'
import { Textarea } from '../components/ui/Textarea'
import { Masthead } from '../components/Masthead'
import { Stepper, STAGES } from '../components/Stepper'
import { Dock } from '../components/Dock'
import { IntakeSection } from '../components/IntakeSection'
import {
  IntakeField,
  RadioOptions,
  CheckboxOptions,
  SectionPremise,
} from '../components/IntakeField'
import { DynList } from '../components/DynList'
import { NavButtons } from '../components/NavButtons'
import { PageFooter } from '../components/PageFooter'
import { ContextusWidget } from '../components/ContextusWidget'

/**
 * Briefer intake form. Layout mirrors the original `lorenza.html`:
 *
 *   Masthead (logo + title hero + lede + tagline + meta strip)
 *   Stepper  (13 sticky stage chips at the top)
 *   Dock     (sticky completion bar + Save Draft / Generate buttons)
 *   13 numbered <IntakeSection>s with the canonical eyebrow + title
 *
 * Field names are snake_case to match the backend's `BriefData` Pydantic
 * schema (`service/app/schemas/brief.py`) so the JSON round-trips with
 * zero translation.
 *
 * Multi-item collections (KPIs, stakeholders, risks…) render as DynList
 * rows. Multi-select fields use CheckboxOptions chips (mirrors the
 * original `.field-options[data-type="multi"]` markup).
 */
interface IntakeBody {
  /* 01 Strategic */
  app_name?: string
  business_problem?: string
  why_now?: string
  build_buy?: string
  budget?: string
  /* 02 Users */
  user_count?: string
  out_of_scope?: string
  a11y?: string
  languages?: string[]
  channels?: string[]
  /* 03 AI */
  ai_capability?: string[]
  model_preference?: string
  ai_exclusions?: string
  hallucination_tolerance?: string
  determinism?: string
  fine_tuning?: string
  eval_strategy?: string
  bias?: string
  /* 04 Data */
  data_types?: string[]
  data_volume?: string
  data_freshness?: string
  data_quality?: string
  data_validation?: string
  data_classification?: string[]
  data_residency?: string
  data_rights?: string
  /* 05 Security */
  auth?: string
  mfa?: string
  authz?: string
  tenancy?: string
  sessions?: string
  encryption?: string[]
  secrets?: string
  prompt_security?: string
  pii_handling?: string
  /* 06 Memory */
  history_retention?: string
  user_memory?: string
  audit_trail?: string
  deletion_rights?: string
  backup_dr?: string
  /* 07 Integrations */
  imports?: string
  exports?: string
  api_exposure?: string
  webhooks?: string
  integration_errors?: string
  /* 08 Scalability */
  volume?: string
  concurrency?: string
  latency?: string
  streaming?: string
  cost_ceiling?: string
  caching?: string[]
  architecture?: string
  geo?: string
  /* 09 Governance */
  regulations?: string[]
  ai_risk?: string
  hitl?: string
  explainability?: string
  consent?: string
  vendor_gov?: string
  /* 10 Operations */
  slo?: string
  deployment?: string
  observability?: string
  alerting?: string
  devops?: string
  support?: string
  feedback?: string
  change_mgmt?: string
  incident?: string
  /* 11 UX */
  tone?: string
  citations?: string
  confidence?: string
  disclosure?: string
  error_ux?: string
  /* 12 Delivery */
  team?: string
  dependencies?: string
  sla?: string
  open_questions?: string
  notes?: string
  /* Multi-item collections (lorenza.html `data-dyn` lists). Each is an
   * array of column-keyed records. */
  kpis?: Record<string, string>[]
  stakeholders?: Record<string, string>[]
  assumptions?: Record<string, string>[]
  personas?: Record<string, string>[]
  use_cases?: Record<string, string>[]
  data_sources?: Record<string, string>[]
  inbound?: Record<string, string>[]
  outbound?: Record<string, string>[]
  risks?: Record<string, string>[]
  milestones?: Record<string, string>[]
}

/**
 * Whole-form field roster, in canonical order. Drives both the dock's
 * completion percent and the readiness snapshot the backend later
 * computes. Multi-select fields count once; dyn-lists count once.
 */
const ALL_FIELD_KEYS: ReadonlyArray<keyof IntakeBody> = [
  // 01
  'app_name', 'business_problem', 'why_now', 'kpis', 'stakeholders',
  'build_buy', 'budget', 'assumptions',
  // 02
  'personas', 'user_count', 'use_cases', 'out_of_scope',
  'languages', 'channels', 'a11y',
  // 03
  'ai_capability', 'model_preference', 'ai_exclusions',
  'hallucination_tolerance', 'determinism', 'fine_tuning',
  'eval_strategy', 'bias',
  // 04
  'data_sources', 'data_types', 'data_volume', 'data_freshness',
  'data_quality', 'data_validation', 'data_classification',
  'data_residency', 'data_rights',
  // 05
  'auth', 'mfa', 'authz', 'tenancy', 'sessions',
  'encryption', 'secrets', 'prompt_security', 'pii_handling',
  // 06
  'history_retention', 'user_memory', 'audit_trail',
  'deletion_rights', 'backup_dr',
  // 07
  'inbound', 'outbound', 'imports', 'exports', 'api_exposure',
  'webhooks', 'integration_errors',
  // 08
  'volume', 'concurrency', 'latency', 'streaming', 'cost_ceiling',
  'caching', 'architecture', 'geo',
  // 09
  'regulations', 'ai_risk', 'hitl', 'explainability', 'consent',
  'vendor_gov', 'risks',
  // 10
  'slo', 'deployment', 'observability', 'alerting', 'devops',
  'support', 'feedback', 'change_mgmt', 'incident',
  // 11
  'tone', 'citations', 'confidence', 'disclosure', 'error_ux',
  // 12
  'milestones', 'team', 'dependencies', 'sla', 'open_questions', 'notes',
]

/** Map from a stage key to the previous stage key, used for nav button navigation. */
const STAGE_ORDER = STAGES.map((s) => s.key)
function prevStage(key: string): string | undefined {
  const i = STAGE_ORDER.indexOf(key)
  return i > 0 ? STAGE_ORDER[i - 1] : undefined
}

export function Intake() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [body, setBody] = useState<IntakeBody>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeStage, setActiveStage] = useState<string>(STAGES[0]?.key ?? 'strategic')
  const formRef = useRef<HTMLFormElement>(null)

  function set<K extends keyof IntakeBody>(key: K, value: IntakeBody[K]) {
    setBody((prev) => ({ ...prev, [key]: value }))
  }

  /** Append an empty row to a dynamic list — the Contextus widget's row-mode
   *  "Add row" calls this through window.ContextusHost.addDynItem. */
  function addDynItem(key: string) {
    setBody((prev) => {
      const cur = (prev as Record<string, unknown>)[key]
      const list = Array.isArray(cur) ? cur : []
      return { ...prev, [key]: [...list, {}] } as IntakeBody
    })
  }

  function goToStage(key: string) {
    setActiveStage(key)
    const el = document.getElementById(`section-${key}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function resetForm() {
    if (!window.confirm(t('dock.reset_confirm', '¿Limpiar todos los campos del formulario?'))) return
    setBody({})
    goToStage(STAGES[0]?.key ?? 'strategic')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const created = await api<BriefDetail>('/api/v1/briefs', {
        method: 'POST',
        body: JSON.stringify({ data: body }),
      })
      navigate(`/briefs/${created.id}`, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  /** Completion metrics, recomputed cheaply on every render. */
  const { filled, total, pct } = useMemo(() => {
    const totalCount = ALL_FIELD_KEYS.length
    const filledCount = ALL_FIELD_KEYS.reduce((acc, key) => {
      const v = body[key]
      if (Array.isArray(v)) return acc + (v.length > 0 ? 1 : 0)
      if (typeof v === 'string') return acc + (v.trim().length > 0 ? 1 : 0)
      return acc + (v != null ? 1 : 0)
    }, 0)
    return {
      filled: filledCount,
      total: totalCount,
      pct: Math.round((filledCount / totalCount) * 100),
    }
  }, [body])

  const activeStageMeta =
    STAGES.find((s) => s.key === activeStage) ?? STAGES[0] ?? {
      num: '01',
      key: 'strategic',
      labelKey: 'intake.stepper.strategic',
    }

  /** Helper for emitting a `<NavButtons>` with prev/next wired to the stepper. */
  function Nav({ stage, nextKey, nextLabel }: { stage: string; nextKey?: string; nextLabel?: string }) {
    const prev = prevStage(stage)
    return (
      <NavButtons
        prevDisabled={!prev}
        onPrev={prev ? () => goToStage(prev) : undefined}
        nextLabel={nextLabel}
        onNext={nextKey ? () => goToStage(nextKey) : undefined}
      />
    )
  }

  return (
    <>
      <Masthead />
      <Stepper active={activeStage} onSelect={goToStage} />
      <Dock
        stage={`Stage ${activeStageMeta.num} — ${t(activeStageMeta.labelKey, activeStageMeta.key)}`}
        pct={pct}
        filled={filled}
        total={total}
        onSaveDraft={() => formRef.current?.requestSubmit()}
        onReset={resetForm}
        onGenerate={() => formRef.current?.requestSubmit()}
      />

      <form ref={formRef} onSubmit={handleSubmit}>
        {/* Contextus in-form AI helper (Epic 10) — sets window.ContextusHost
            from live form state + loads the widget asset. Renders nothing. */}
        <ContextusWidget body={body as Record<string, unknown>} onAddDynItem={addDynItem} />
        {/* ── 01. Strategic Context ─────────────────────────────────── */}
        <IntakeSection
          num={1}
          active={activeStage === 'strategic'}
          label={t('intake.sections.strategic_label', 'Discovery · Phase One')}
          title={t('intake.sections.strategic_title', 'Strategic Context & Business Intent')}
          subtitle={t(
            'intake.sections.strategic_sub',
            'Why this exists. The questions every sponsor can answer — and that every developer wishes had been asked first.',
          )}
          pct={pct}
        >
          <div id="section-strategic" />
          <SectionPremise
            quote={t(
              'intake.sections.strategic_premise',
              'If a sponsor cannot describe the business problem in two sentences without using the word "AI," the project is not yet ready to scope.',
            )}
            citation={t('intake.section_premise_citation', '— Section premise')}
          />

          <IntakeField
            num="1.01"
            label={t('intake.fields.app_name')}
            help={t('intake.help.app_name', 'A short codename for the build. Final branding can come later.')}
            required
          >
            <Input data-key="app_name"
              value={body.app_name ?? ''}
              placeholder={t('intake.ph.app_name', 'e.g., AURA — Underwriting Risk Assistant')}
              onChange={(e) => set('app_name', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="1.02"
            label={t('intake.fields.business_problem')}
            help={t(
              'intake.help.business_problem',
              'Describe what hurts today, in two or three sentences, without mentioning AI, ML, or any technology. If you cannot, the problem is not yet defined.',
            )}
            required
            tags={['critical']}
          >
            <Textarea data-key="business_problem"
              rows={4}
              value={body.business_problem ?? ''}
              placeholder={t(
                'intake.ph.business_problem',
                'Underwriters spend 60% of their time pulling data from seven systems before they can begin assessment, which means deals close in 14 days when competitors close in 4…',
              )}
              onChange={(e) => set('business_problem', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="1.03"
            label={t('intake.fields.why_now')}
            help={t(
              'intake.help.why_now',
              'What changed — internally or in the market — that makes this urgent this quarter rather than next year?',
            )}
            tags={['strategic']}
          >
            <Textarea data-key="why_now"
              rows={3}
              value={body.why_now ?? ''}
              placeholder={t(
                'intake.ph.why_now',
                'Regulatory deadline, competitor launch, cost pressure, executive mandate…',
              )}
              onChange={(e) => set('why_now', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="1.04"
            label={t('intake.fields.kpis', 'Success criteria & KPIs')}
            help={t(
              'intake.help.kpis',
              "Each row: the metric, current baseline, target at 12 months, how it's measured. One number per row.",
            )}
            required
            tags={['new']}
          >
            <DynList
              name="kpis"
              rows={body.kpis ?? []}
              cols={[
                { key: 'metric',   placeholder: t('intake.ph.kpi.metric',   'Metric') },
                { key: 'baseline', placeholder: t('intake.ph.kpi.baseline', 'Baseline today') },
                { key: 'target',   placeholder: t('intake.ph.kpi.target',   'Target at 12 mo') },
                { key: 'measure',  placeholder: t('intake.ph.kpi.measure',  'How it is measured') },
              ]}
              addLabel={t('intake.dyn.add_kpi', '+ Add KPI')}
              onChange={(rows) => set('kpis', rows)}
            />
          </IntakeField>

          <IntakeField
            num="1.05"
            label={t('intake.fields.stakeholders', 'Key stakeholders')}
            help={t(
              'intake.help.stakeholders',
              'Who owns what. Naming the executive sponsor, technical owner, and compliance lead now prevents weeks of routing later.',
            )}
            required
            tags={['new']}
          >
            <DynList
              name="stakeholders"
              rows={body.stakeholders ?? []}
              cols={[
                { key: 'name',    placeholder: t('intake.ph.sh.name',    'Name') },
                { key: 'role',    placeholder: t('intake.ph.sh.role',    'Role') },
                { key: 'contact', placeholder: t('intake.ph.sh.contact', 'Contact') },
              ]}
              addLabel={t('intake.dyn.add_stakeholder', '+ Add Stakeholder')}
              onChange={(rows) => set('stakeholders', rows)}
            />
          </IntakeField>

          <IntakeField
            num="1.06"
            label={t('intake.fields.build_buy', 'Build vs. buy posture')}
            help={t(
              'intake.help.build_buy',
              'How does the organization feel about building proprietary AI versus integrating commercial tools?',
            )}
          >
            <RadioOptions
              name="build_buy"
              value={body.build_buy}
              onChange={(v) => set('build_buy', v)}
              options={[
                { value: 'build',    label: t('intake.opts.build_buy.build.label', 'Build proprietary'),
                                     desc:  t('intake.opts.build_buy.build.desc', 'Differentiation matters; willing to invest') },
                { value: 'buy',      label: t('intake.opts.build_buy.buy.label', 'Buy commercial'),
                                     desc:  t('intake.opts.build_buy.buy.desc', 'Speed and risk reduction over uniqueness') },
                { value: 'hybrid',   label: t('intake.opts.build_buy.hybrid.label', 'Hybrid / wrap'),
                                     desc:  t('intake.opts.build_buy.hybrid.desc', 'Commercial models, proprietary orchestration') },
                { value: 'undecided',label: t('intake.opts.build_buy.undecided.label', 'Undecided'),
                                     desc:  t('intake.opts.build_buy.undecided.desc', 'Open to recommendation') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="1.07"
            label={t('intake.fields.budget', 'Budget envelope (Year 1, all-in)')}
            help={t(
              'intake.help.budget',
              'Order of magnitude is fine. Includes infrastructure, licenses, services, and internal time.',
            )}
            tags={['critical']}
          >
            <RadioOptions
              name="budget"
              value={body.budget}
              onChange={(v) => set('budget', v)}
              options={[
                { value: 'under_50k', label: t('intake.opts.budget.under_50k.label', 'Under $50K'),
                                      desc:  t('intake.opts.budget.under_50k.desc', 'Pilot / proof of concept') },
                { value: '50_250k',   label: t('intake.opts.budget.50_250k.label', '$50K – $250K'),
                                      desc:  t('intake.opts.budget.50_250k.desc', 'Department-scale build') },
                { value: '250_1m',    label: t('intake.opts.budget.250_1m.label', '$250K – $1M'),
                                      desc:  t('intake.opts.budget.250_1m.desc', 'Strategic initiative') },
                { value: '1m_plus',   label: t('intake.opts.budget.1m_plus.label', '$1M+'),
                                      desc:  t('intake.opts.budget.1m_plus.desc', 'Enterprise transformation') },
                { value: 'tbd',       label: t('intake.opts.budget.tbd.label', 'To be defined'),
                                      desc:  t('intake.opts.budget.tbd.desc', 'Awaiting business case') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="1.08"
            label={t('intake.fields.assumptions', 'Assumptions & constraints')}
            help={t(
              'intake.help.assumptions',
              'Each row: type (assumption or constraint), description, impact if invalidated. Surfacing these now is dramatically cheaper than discovering them mid-build.',
            )}
            tags={['new']}
          >
            <DynList
              name="assumptions"
              rows={body.assumptions ?? []}
              cols={[
                { key: 'type',        placeholder: t('intake.ph.asn.type',        'Type') },
                { key: 'description', placeholder: t('intake.ph.asn.description', 'Description') },
                { key: 'impact',      placeholder: t('intake.ph.asn.impact',      'Impact if invalidated') },
              ]}
              addLabel={t('intake.dyn.add_assumption', '+ Add Assumption / Constraint')}
              onChange={(rows) => set('assumptions', rows)}
            />
          </IntakeField>

          <Nav stage="strategic" nextKey="users" nextLabel={t('intake.next.users', 'Users & Use Cases')} />
        </IntakeSection>

        {/* ── 02. Users, Personas & Use Cases ─────────────────────── */}
        <IntakeSection
          num={2}
          active={activeStage === 'users'}
          label={t('intake.sections.users_label', 'Discovery · Phase Two')}
          title={t('intake.sections.users_title', 'Users, Personas & Use Cases')}
          subtitle={t(
            'intake.sections.users_sub',
            'Who actually touches this thing, in what state of mind, and how often.',
          )}
        >
          <div id="section-users" />

          <IntakeField
            num="2.01"
            label={t('intake.fields.personas', 'User personas')}
            help={t(
              'intake.help.personas',
              'Each row: persona name, role and tenure, technical fluency, working environment, primary motivation.',
            )}
            required
            tags={['new']}
          >
            <DynList
              name="personas"
              rows={body.personas ?? []}
              cols={[
                { key: 'name',        placeholder: t('intake.ph.persona.name',        'Persona name') },
                { key: 'description', placeholder: t('intake.ph.persona.description', 'Role, tenure, fluency, environment, motivation') },
              ]}
              addLabel={t('intake.dyn.add_persona', '+ Add Persona')}
              onChange={(rows) => set('personas', rows)}
            />
          </IntakeField>

          <IntakeField
            num="2.02"
            label={t('intake.fields.user_count', 'Estimated active users')}
            help={t(
              'intake.help.user_count',
              'At launch versus 12 months out. This drives almost every infrastructure decision.',
            )}
            required
          >
            <Input data-key="user_count"
              value={body.user_count ?? ''}
              placeholder={t('intake.ph.user_count', 'Launch: 25 users · Year 1: 200 · Year 2: 500')}
              onChange={(e) => set('user_count', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="2.03"
            label={t('intake.fields.use_cases', 'Top use cases, ranked')}
            help={t(
              'intake.help.use_cases',
              'Each row: use case name, description, pre-condition, post-condition. Use cases drive scope; vague ambitions sink it.',
            )}
            required
            tags={['new']}
          >
            <DynList
              name="use_cases"
              rows={body.use_cases ?? []}
              cols={[
                { key: 'name',   placeholder: t('intake.ph.use_case.name',   'Use case') },
                { key: 'detail', placeholder: t('intake.ph.use_case.detail', 'Pre / post-condition') },
              ]}
              addLabel={t('intake.dyn.add_use_case', '+ Add Use Case')}
              onChange={(rows) => set('use_cases', rows)}
            />
          </IntakeField>

          <IntakeField
            num="2.04"
            label={t('intake.fields.out_of_scope', 'Out-of-scope use cases & exclusions')}
            help={t(
              'intake.help.out_of_scope',
              'Things people will ask for that the system explicitly will not do. Naming these now prevents scope creep later.',
            )}
            tags={['new']}
          >
            <Textarea data-key="out_of_scope"
              rows={3}
              value={body.out_of_scope ?? ''}
              placeholder={t(
                'intake.ph.out_of_scope',
                'Final policy issuance, customer-facing chat, automated decisioning without human review...',
              )}
              onChange={(e) => set('out_of_scope', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="2.05"
            label={t('intake.fields.languages', 'Languages supported at launch')}
            help={t(
              'intake.help.languages',
              'Particularly relevant for LATAM deployments where Spanish, Portuguese, and English often coexist.',
            )}
          >
            <CheckboxOptions
              name="languages"
              values={body.languages ?? []}
              onChange={(v) => set('languages', v)}
              options={[
                { value: 'english',    label: t('intake.opts.languages.english.label',    'English') },
                { value: 'spanish',    label: t('intake.opts.languages.spanish.label',    'Spanish') },
                { value: 'portuguese', label: t('intake.opts.languages.portuguese.label', 'Portuguese') },
                { value: 'other',      label: t('intake.opts.languages.other.label',      'Other'),
                                       desc:  t('intake.opts.languages.other.desc',       'Specify in notes below') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="2.06"
            label={t('intake.fields.channels', 'Channels of access')}
            help={t('intake.help.channels', 'Where the user encounters the system.')}
          >
            <CheckboxOptions
              name="channels"
              values={body.channels ?? []}
              onChange={(v) => set('channels', v)}
              options={[
                { value: 'web',      label: t('intake.opts.channels.web.label', 'Web application') },
                { value: 'mobile',   label: t('intake.opts.channels.mobile.label', 'Mobile (iOS / Android)') },
                { value: 'api',      label: t('intake.opts.channels.api.label', 'API / programmatic') },
                { value: 'embedded', label: t('intake.opts.channels.embedded.label', 'Embedded in existing app'),
                                     desc:  t('intake.opts.channels.embedded.desc',  'Salesforce, ServiceNow, Teams, Slack...') },
                { value: 'voice',    label: t('intake.opts.channels.voice.label', 'Voice / conversational') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="2.07"
            label={t('intake.fields.a11y', 'Accessibility requirements')}
            help={t(
              'intake.help.a11y',
              'Beyond WCAG, particularly relevant for AI: screen-reader handling of streamed responses, keyboard navigation through suggestions, color-independent confidence signals.',
            )}
            tags={['new']}
          >
            <RadioOptions
              name="a11y"
              value={body.a11y}
              onChange={(v) => set('a11y', v)}
              options={[
                { value: 'wcag_aa',  label: t('intake.opts.a11y.wcag_aa.label', 'WCAG 2.2 AA') },
                { value: 'wcag_aaa', label: t('intake.opts.a11y.wcag_aaa.label', 'WCAG 2.2 AAA') },
                { value: 'basic',    label: t('intake.opts.a11y.basic.label', 'Basic / not formalized') },
              ]}
            />
          </IntakeField>

          <Nav stage="users" nextKey="ai" nextLabel={t('intake.next.ai', 'AI Strategy')} />
        </IntakeSection>

        {/* ── 03. AI Capability & Model Strategy ────────────────────── */}
        <IntakeSection
          num={3}
          active={activeStage === 'ai'}
          label={t('intake.sections.ai_label', 'Discovery · Phase Three')}
          title={t('intake.sections.ai_title', 'AI Capability & Model Strategy')}
          subtitle={t(
            'intake.sections.ai_sub',
            'What the AI is actually doing under the hood. The first place vague briefs collide with engineering reality.',
          )}
        >
          <div id="section-ai" />

          <IntakeField
            num="3.01"
            label={t('intake.fields.ai_capability', 'Primary AI capabilities')}
            help={t('intake.help.ai_capability', 'Most apps blend two or three; identify all that apply.')}
            required
          >
            <CheckboxOptions
              name="ai_capability"
              values={body.ai_capability ?? []}
              onChange={(v) => set('ai_capability', v)}
              options={[
                { value: 'generation',     label: t('intake.opts.ai_capability.generation.label', 'Content generation'),
                                           desc:  t('intake.opts.ai_capability.generation.desc',  'Drafts, summaries, copy') },
                { value: 'rag',            label: t('intake.opts.ai_capability.rag.label', 'RAG / retrieval Q&A'),
                                           desc:  t('intake.opts.ai_capability.rag.desc',  'Answer from company knowledge') },
                { value: 'classification', label: t('intake.opts.ai_capability.classification.label', 'Classification / scoring'),
                                           desc:  t('intake.opts.ai_capability.classification.desc',  'Categorize, prioritize, score') },
                { value: 'extraction',     label: t('intake.opts.ai_capability.extraction.label', 'Data extraction'),
                                           desc:  t('intake.opts.ai_capability.extraction.desc',  'Structure from unstructured') },
                { value: 'agents',         label: t('intake.opts.ai_capability.agents.label', 'Agentic workflows'),
                                           desc:  t('intake.opts.ai_capability.agents.desc',  'Multi-step, tool-using') },
                { value: 'forecasting',    label: t('intake.opts.ai_capability.forecasting.label', 'Forecasting / prediction'),
                                           desc:  t('intake.opts.ai_capability.forecasting.desc',  'Time-series, demand, risk') },
                { value: 'vision',         label: t('intake.opts.ai_capability.vision.label', 'Computer vision'),
                                           desc:  t('intake.opts.ai_capability.vision.desc',  'Image, document, video') },
                { value: 'speech',         label: t('intake.opts.ai_capability.speech.label', 'Speech / audio'),
                                           desc:  t('intake.opts.ai_capability.speech.desc',  'Transcription, voice') },
                { value: 'anomaly',        label: t('intake.opts.ai_capability.anomaly.label', 'Anomaly detection'),
                                           desc:  t('intake.opts.ai_capability.anomaly.desc',  'Outliers, fraud signals') },
                { value: 'recommend',      label: t('intake.opts.ai_capability.recommend.label', 'Recommendation'),
                                           desc:  t('intake.opts.ai_capability.recommend.desc',  'Personalization, ranking') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="3.02"
            label={t('intake.fields.model_preference', 'Model preference / mandate')}
            help={t('intake.help.model_preference', 'Has the organization committed to a provider, or is this open?')}
          >
            <RadioOptions
              name="model_preference"
              value={body.model_preference}
              onChange={(v) => set('model_preference', v)}
              options={[
                { value: 'open',      label: t('intake.opts.model_preference.open.label', 'Open recommendation') },
                { value: 'anthropic', label: t('intake.opts.model_preference.anthropic.label', 'Anthropic / Claude') },
                { value: 'openai',    label: t('intake.opts.model_preference.openai.label', 'OpenAI / Azure OpenAI') },
                { value: 'google',    label: t('intake.opts.model_preference.google.label', 'Google / Vertex / Gemini') },
                { value: 'aws',       label: t('intake.opts.model_preference.aws.label', 'AWS Bedrock') },
                { value: 'oss',       label: t('intake.opts.model_preference.oss.label', 'Open-source / self-hosted'),
                                      desc:  t('intake.opts.model_preference.oss.desc',  'Llama, Mistral, etc.') },
                { value: 'multi',     label: t('intake.opts.model_preference.multi.label', 'Multi-provider abstraction') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="3.03"
            label={t('intake.fields.ai_exclusions', 'AI features & models to EXCLUDE')}
            help={t(
              'intake.help.ai_exclusions',
              'What the system must explicitly NOT do, even if technically possible. Often a regulatory, ethical, or brand constraint. Naming these is as important as naming what it will do.',
            )}
            tags={['new', 'strategic']}
          >
            <Textarea data-key="ai_exclusions"
              rows={3}
              value={body.ai_exclusions ?? ''}
              placeholder={t(
                'intake.ph.ai_exclusions',
                'No facial recognition · No fully automated decisioning · No generative content sent to customers without human review · No emotion detection · No predictive policing or pre-crime applications...',
              )}
              onChange={(e) => set('ai_exclusions', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="3.04"
            label={t('intake.fields.hallucination_tolerance', 'Hallucination tolerance')}
            help={t(
              'intake.help.hallucination_tolerance',
              'How catastrophic is a confident-sounding wrong answer? This determines guardrails, evaluation rigor, and whether human review is mandatory.',
            )}
            tags={['critical']}
          >
            <RadioOptions
              name="hallucination_tolerance"
              value={body.hallucination_tolerance}
              onChange={(v) => set('hallucination_tolerance', v)}
              options={[
                { value: 'zero',     label: t('intake.opts.hallucination_tolerance.zero.label', 'Zero tolerance'),
                                     desc:  t('intake.opts.hallucination_tolerance.zero.desc',  'Regulatory, medical, financial advice') },
                { value: 'low',      label: t('intake.opts.hallucination_tolerance.low.label', 'Low — human in loop'),
                                     desc:  t('intake.opts.hallucination_tolerance.low.desc',  'Reviewer always present') },
                { value: 'moderate', label: t('intake.opts.hallucination_tolerance.moderate.label', 'Moderate'),
                                     desc:  t('intake.opts.hallucination_tolerance.moderate.desc',  'Internal use, errors recoverable') },
                { value: 'high',     label: t('intake.opts.hallucination_tolerance.high.label', 'High — creative / exploratory'),
                                     desc:  t('intake.opts.hallucination_tolerance.high.desc',  'Brainstorming, ideation') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="3.05"
            label={t('intake.fields.determinism', 'Determinism requirements')}
            help={t(
              'intake.help.determinism',
              'Must the same input always produce the same output? Critical for audit, reproducibility, regulatory contexts.',
            )}
          >
            <RadioOptions
              name="determinism"
              value={body.determinism}
              onChange={(v) => set('determinism', v)}
              options={[
                { value: 'strict',   label: t('intake.opts.determinism.strict.label', 'Strict — must reproduce') },
                { value: 'logged',   label: t('intake.opts.determinism.logged.label', 'Logged — replay possible') },
                { value: 'flexible', label: t('intake.opts.determinism.flexible.label', 'Flexible — variation acceptable') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="3.06"
            label={t('intake.fields.fine_tuning', 'Fine-tuning or custom training expected?')}
            help={t(
              'intake.help.fine_tuning',
              "Most enterprise apps don't need it. Confirming early prevents architectural over-investment.",
            )}
          >
            <RadioOptions
              name="fine_tuning"
              value={body.fine_tuning}
              onChange={(v) => set('fine_tuning', v)}
              options={[
                { value: 'no',    label: t('intake.opts.fine_tuning.no.label', 'No — prompt + retrieval only') },
                { value: 'maybe', label: t('intake.opts.fine_tuning.maybe.label', 'Possibly — evaluate later') },
                { value: 'yes',   label: t('intake.opts.fine_tuning.yes.label', 'Yes — domain adaptation needed') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="3.07"
            label={t('intake.fields.eval_strategy', 'Evaluation strategy')}
            help={t(
              'intake.help.eval_strategy',
              'How will quality be measured before launch and monitored afterward?',
            )}
          >
            <Textarea data-key="eval_strategy"
              rows={3}
              value={body.eval_strategy ?? ''}
              placeholder={t(
                'intake.ph.eval_strategy',
                'Golden dataset of 500 historical cases, human-graded weekly samples, automated regression on prompt changes, user thumbs-up/down with mandatory comment on negatives...',
              )}
              onChange={(e) => set('eval_strategy', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="3.08"
            label={t('intake.fields.bias', 'Bias mitigation strategy')}
            help={t(
              'intake.help.bias',
              "Especially critical for any system affecting people's access to services, credit, employment, or care.",
            )}
            tags={['strategic']}
          >
            <Textarea data-key="bias"
              rows={3}
              value={body.bias ?? ''}
              placeholder={t(
                'intake.ph.bias',
                'Pre-launch fairness audit across protected attributes; ongoing disparate-impact monitoring; quarterly fairness review with compliance...',
              )}
              onChange={(e) => set('bias', e.target.value)}
            />
          </IntakeField>

          <Nav stage="ai" nextKey="data" nextLabel={t('intake.next.data', 'Data & Knowledge')} />
        </IntakeSection>

        {/* ── 04. Data, Knowledge & Sources ────────────────────────── */}
        <IntakeSection
          num={4}
          active={activeStage === 'data'}
          label={t('intake.sections.data_label', 'Discovery · Phase Four')}
          title={t('intake.sections.data_title', 'Data, Knowledge & Sources')}
          subtitle={t(
            'intake.sections.data_sub',
            'The fuel. Most AI projects fail here, not at the model layer.',
          )}
        >
          <div id="section-data" />
          <SectionPremise
            quote={t(
              'intake.sections.data_premise',
              '"We have all the data" is the single most expensive sentence in enterprise AI. The data exists; what\'s missing is access, structure, freshness, and rights.',
            )}
            citation={t('intake.sections.data_premise_cite', '— Recurring observation')}
          />

          <IntakeField
            num="4.01"
            label={t('intake.fields.data_sources', 'Data sources & owners')}
            help={t(
              'intake.help.data_sources',
              'Each row: system name, data type, owner / steward, access method. Be specific.',
            )}
            required
            tags={['new']}
          >
            <DynList
              name="data_sources"
              rows={body.data_sources ?? []}
              cols={[
                { key: 'system',    placeholder: t('intake.ph.data_source.system',    'System') },
                { key: 'data_type', placeholder: t('intake.ph.data_source.data_type', 'Data type') },
                { key: 'owner',     placeholder: t('intake.ph.data_source.owner',     'Owner / steward') },
                { key: 'access',    placeholder: t('intake.ph.data_source.access',    'Access method') },
              ]}
              addLabel={t('intake.dyn.add_data_source', '+ Add Data Source')}
              onChange={(rows) => set('data_sources', rows)}
            />
          </IntakeField>

          <IntakeField
            num="4.02"
            label={t('intake.fields.data_types', 'Data types processed')}
            help={t('intake.help.data_types', 'All formats the system will handle.')}
          >
            <CheckboxOptions
              name="data_types"
              values={body.data_types ?? []}
              onChange={(v) => set('data_types', v)}
              options={[
                { value: 'text_struct',   label: t('intake.opts.data_types.text_struct.label', 'Structured text'),
                                          desc:  t('intake.opts.data_types.text_struct.desc',  'CSV, SQL, JSON') },
                { value: 'text_unstruct', label: t('intake.opts.data_types.text_unstruct.label', 'Unstructured text'),
                                          desc:  t('intake.opts.data_types.text_unstruct.desc',  'Documents, emails, chat') },
                { value: 'images',        label: t('intake.opts.data_types.images.label',     'Images') },
                { value: 'video',         label: t('intake.opts.data_types.video.label',      'Video') },
                { value: 'audio',         label: t('intake.opts.data_types.audio.label',      'Audio') },
                { value: 'behavior',      label: t('intake.opts.data_types.behavior.label',   'User behavior / telemetry') },
                { value: 'timeseries',    label: t('intake.opts.data_types.timeseries.label', 'Time-series') },
                { value: 'geospatial',    label: t('intake.opts.data_types.geospatial.label', 'Geospatial') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="4.03"
            label={t('intake.fields.data_volume', 'Data volume at launch')}
            help={t('intake.help.data_volume', 'Order of magnitude: documents, rows, GB, hours of audio, etc.')}
          >
            <Input data-key="data_volume"
              value={body.data_volume ?? ''}
              placeholder={t('intake.ph.data_volume', '3,000 PDFs (~12GB), 2M CRM records, 80M transactional rows')}
              onChange={(e) => set('data_volume', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="4.04"
            label={t('intake.fields.data_freshness', 'Data freshness requirement')}
            help={t('intake.help.data_freshness', 'How recent must the data be for answers to be useful?')}
          >
            <RadioOptions
              name="data_freshness"
              value={body.data_freshness}
              onChange={(v) => set('data_freshness', v)}
              options={[
                { value: 'realtime', label: t('intake.opts.data_freshness.realtime.label', 'Real-time'),
                                     desc:  t('intake.opts.data_freshness.realtime.desc',  'Streaming / sub-second') },
                { value: 'hourly',   label: t('intake.opts.data_freshness.hourly.label', 'Hourly') },
                { value: 'daily',    label: t('intake.opts.data_freshness.daily.label', 'Daily') },
                { value: 'weekly',   label: t('intake.opts.data_freshness.weekly.label', 'Weekly+') },
                { value: 'static',   label: t('intake.opts.data_freshness.static.label', 'Static / point-in-time') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="4.05"
            label={t('intake.fields.data_quality', 'Data quality reality check')}
            help={t('intake.help.data_quality', 'Honest assessment. The right answer is rarely "excellent."')}
            tags={['strategic']}
          >
            <RadioOptions
              name="data_quality"
              value={body.data_quality}
              onChange={(v) => set('data_quality', v)}
              options={[
                { value: 'excellent', label: t('intake.opts.data_quality.excellent.label', 'Excellent — clean, governed') },
                { value: 'good',      label: t('intake.opts.data_quality.good.label', 'Good — minor gaps') },
                { value: 'mixed',     label: t('intake.opts.data_quality.mixed.label', 'Mixed — some clean, some not') },
                { value: 'poor',      label: t('intake.opts.data_quality.poor.label', 'Poor — significant cleanup needed') },
                { value: 'unknown',   label: t('intake.opts.data_quality.unknown.label', 'Unknown — not yet assessed') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="4.06"
            label={t('intake.fields.data_validation', 'Data validation rules')}
            help={t('intake.help.data_validation', 'What rules must input data adhere to before it enters the AI pipeline?')}
            tags={['new']}
          >
            <Textarea data-key="data_validation"
              rows={3}
              value={body.data_validation ?? ''}
              placeholder={t(
                'intake.ph.data_validation',
                'Required fields enforced at ingestion · Schema validation against contract · File size limits 50MB per document · PII detection and quarantine · Deduplication on (account_id, document_hash)...',
              )}
              onChange={(e) => set('data_validation', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="4.07"
            label={t('intake.fields.data_classification', 'Data classification & sensitivity')}
            help={t('intake.help.data_classification', "What's the most sensitive data the system will touch?")}
          >
            <CheckboxOptions
              name="data_classification"
              values={body.data_classification ?? []}
              onChange={(v) => set('data_classification', v)}
              options={[
                { value: 'public',    label: t('intake.opts.data_classification.public.label', 'Public') },
                { value: 'internal',  label: t('intake.opts.data_classification.internal.label', 'Internal / confidential') },
                { value: 'pii',       label: t('intake.opts.data_classification.pii.label', 'PII — personal data') },
                { value: 'phi',       label: t('intake.opts.data_classification.phi.label', 'PHI — health records') },
                { value: 'financial', label: t('intake.opts.data_classification.financial.label', 'Financial / PCI') },
                { value: 'ip',        label: t('intake.opts.data_classification.ip.label', 'Trade secrets / IP') },
                { value: 'regulated', label: t('intake.opts.data_classification.regulated.label', 'Regulated content'),
                                      desc:  t('intake.opts.data_classification.regulated.desc',  'Legal, contracts') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="4.08"
            label={t('intake.fields.data_residency', 'Data residency & sovereignty')}
            help={t(
              'intake.help.data_residency',
              'Where is the data legally required to live? Particularly relevant for LATAM (LGPD Brazil, Habeas Data Colombia, México LFPDPPP), EU (GDPR), and regulated sectors globally.',
            )}
            tags={['critical']}
          >
            <Textarea data-key="data_residency"
              rows={2}
              value={body.data_residency ?? ''}
              placeholder={t(
                'intake.ph.data_residency',
                'Customer data must remain in Colombia per Habeas Data; financial records must remain in country of origin per local banking regulations...',
              )}
              onChange={(e) => set('data_residency', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="4.09"
            label={t('intake.fields.data_rights', 'Data licensing & rights to use for AI')}
            help={t(
              'intake.help.data_rights',
              'Has anyone confirmed the organization has the right to feed this data into models — including third-party providers?',
            )}
          >
            <RadioOptions
              name="data_rights"
              value={body.data_rights}
              onChange={(v) => set('data_rights', v)}
              options={[
                { value: 'confirmed',   label: t('intake.opts.data_rights.confirmed.label', 'Confirmed by legal') },
                { value: 'in_review',   label: t('intake.opts.data_rights.in_review.label', 'In legal review') },
                { value: 'not_started', label: t('intake.opts.data_rights.not_started.label', 'Not yet assessed') },
                { value: 'restricted',  label: t('intake.opts.data_rights.restricted.label', 'Known restrictions exist') },
              ]}
            />
          </IntakeField>

          <Nav stage="data" nextKey="security" nextLabel={t('intake.next.security', 'Security')} />
        </IntakeSection>

        {/* ── 05. Security, Identity & Sessions ────────────────────── */}
        <IntakeSection
          num={5}
          active={activeStage === 'security'}
          label={t('intake.sections.security_label', 'Discovery · Phase Five')}
          title={t('intake.sections.security_title', 'Security, Identity & Sessions')}
          subtitle={t(
            'intake.sections.security_sub',
            'Who gets in, who sees what, and how the system handles state across time.',
          )}
        >
          <div id="section-security" />

          <IntakeField
            num="5.01"
            label={t('intake.fields.auth', 'Authentication mechanism')}
            help={t('intake.help.auth', 'How users prove who they are.')}
            required
          >
            <RadioOptions
              name="auth"
              value={body.auth}
              onChange={(v) => set('auth', v)}
              options={[
                { value: 'sso_saml', label: t('intake.opts.auth.sso_saml.label', 'SSO via SAML'),
                                     desc:  t('intake.opts.auth.sso_saml.desc',  'Okta, ADFS, Ping') },
                { value: 'sso_oidc', label: t('intake.opts.auth.sso_oidc.label', 'SSO via OIDC'),
                                     desc:  t('intake.opts.auth.sso_oidc.desc',  'Azure AD, Google Workspace') },
                { value: 'local',    label: t('intake.opts.auth.local.label', 'Local auth + MFA') },
                { value: 'api_key',  label: t('intake.opts.auth.api_key.label', 'API key (system-to-system)') },
                { value: 'mixed',    label: t('intake.opts.auth.mixed.label', 'Mixed (SSO users + service)') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="5.02"
            label={t('intake.fields.mfa', 'MFA requirement')}
            help={t('intake.help.mfa', 'Often mandated separately from SSO method.')}
            tags={['new']}
          >
            <RadioOptions
              name="mfa"
              value={body.mfa}
              onChange={(v) => set('mfa', v)}
              options={[
                { value: 'required_all',   label: t('intake.opts.mfa.required_all.label', 'Required for all users') },
                { value: 'required_admin', label: t('intake.opts.mfa.required_admin.label', 'Required for admins only') },
                { value: 'optional',       label: t('intake.opts.mfa.optional.label', 'Optional') },
                { value: 'inherited',      label: t('intake.opts.mfa.inherited.label', 'Inherited from SSO') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="5.03"
            label={t('intake.fields.authz', 'Authorization model')}
            help={t('intake.help.authz', 'How permissions are structured. Affects every read of data.')}
          >
            <RadioOptions
              name="authz"
              value={body.authz}
              onChange={(v) => set('authz', v)}
              options={[
                { value: 'rbac',   label: t('intake.opts.authz.rbac.label', 'RBAC — role-based') },
                { value: 'abac',   label: t('intake.opts.authz.abac.label', 'ABAC — attribute-based') },
                { value: 'rebac',  label: t('intake.opts.authz.rebac.label', 'ReBAC — relationship-based') },
                { value: 'rls',    label: t('intake.opts.authz.rls.label', 'Row-level security') },
                { value: 'hybrid', label: t('intake.opts.authz.hybrid.label', 'Hybrid / custom') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="5.04"
            label={t('intake.fields.tenancy', 'Tenant isolation model')}
            help={t(
              'intake.help.tenancy',
              'If the system serves multiple business units, customers, or organizations.',
            )}
          >
            <RadioOptions
              name="tenancy"
              value={body.tenancy}
              onChange={(v) => set('tenancy', v)}
              options={[
                { value: 'single',         label: t('intake.opts.tenancy.single.label', 'Single tenant') },
                { value: 'multi_logical',  label: t('intake.opts.tenancy.multi_logical.label', 'Multi-tenant, shared infra') },
                { value: 'multi_isolated', label: t('intake.opts.tenancy.multi_isolated.label', 'Multi-tenant, isolated infra') },
                { value: 'dedicated',      label: t('intake.opts.tenancy.dedicated.label', 'Dedicated per tenant') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="5.05"
            label={t('intake.fields.sessions', 'Session management requirements')}
            help={t(
              'intake.help.sessions',
              'How user state, conversation context, and authenticated sessions persist.',
            )}
          >
            <Textarea data-key="sessions"
              rows={3}
              value={body.sessions ?? ''}
              placeholder={t(
                'intake.ph.sessions',
                'Session timeout: 30 min idle, 8h absolute · Conversation history retained 90 days · Cross-device session resume required · Concurrent session limit: 3 per user',
              )}
              onChange={(e) => set('sessions', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="5.06"
            label={t('intake.fields.encryption', 'Encryption requirements')}
            help={t('intake.help.encryption', 'In transit, at rest, and for sensitive fields.')}
          >
            <CheckboxOptions
              name="encryption"
              values={body.encryption ?? []}
              onChange={(v) => set('encryption', v)}
              options={[
                { value: 'tls',       label: t('intake.opts.encryption.tls.label', 'TLS 1.3 in transit') },
                { value: 'rest',      label: t('intake.opts.encryption.rest.label', 'AES-256 at rest') },
                { value: 'field',     label: t('intake.opts.encryption.field.label', 'Field-level encryption') },
                { value: 'bring_key', label: t('intake.opts.encryption.bring_key.label', 'Customer-managed keys (BYOK)') },
                { value: 'hsm',       label: t('intake.opts.encryption.hsm.label', 'HSM-backed key storage') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="5.07"
            label={t('intake.fields.secrets', 'Secrets & credential management')}
            help={t('intake.help.secrets', 'Where API keys, model credentials, and database secrets live.')}
          >
            <Input data-key="secrets"
              value={body.secrets ?? ''}
              placeholder={t('intake.ph.secrets', 'HashiCorp Vault, AWS Secrets Manager, Azure Key Vault...')}
              onChange={(e) => set('secrets', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="5.08"
            label={t('intake.fields.prompt_security', 'Prompt injection & adversarial input defense')}
            help={t(
              'intake.help.prompt_security',
              "AI-specific attack surface. What's the strategy for hostile users trying to manipulate the model? Often missed entirely in traditional security reviews.",
            )}
            tags={['critical']}
          >
            <Textarea data-key="prompt_security"
              rows={3}
              value={body.prompt_security ?? ''}
              placeholder={t(
                'intake.ph.prompt_security',
                'Input sanitization layer · Separate user content from system prompts structurally · Output filtering for PII regurgitation · Rate limiting per user · Anomaly detection on prompt patterns...',
              )}
              onChange={(e) => set('prompt_security', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="5.09"
            label={t('intake.fields.pii_handling', 'PII handling at the prompt boundary')}
            help={t(
              'intake.help.pii_handling',
              "Are you allowed to send personal data to a third-party model? If not, what's the redaction strategy?",
            )}
            tags={['critical']}
          >
            <RadioOptions
              name="pii_handling"
              value={body.pii_handling}
              onChange={(v) => set('pii_handling', v)}
              options={[
                { value: 'full_send',  label: t('intake.opts.pii_handling.full_send.label', 'Send as-is (DPA in place)') },
                { value: 'redact',     label: t('intake.opts.pii_handling.redact.label', 'Redact before sending') },
                { value: 'tokenize',   label: t('intake.opts.pii_handling.tokenize.label', 'Tokenize / pseudonymize') },
                { value: 'local_only', label: t('intake.opts.pii_handling.local_only.label', 'PII never leaves perimeter') },
              ]}
            />
          </IntakeField>

          <Nav stage="security" nextKey="memory" nextLabel={t('intake.next.memory', 'Memory & Retention')} />
        </IntakeSection>

        {/* ── 06. Historical Data, Memory & Retention ──────────────── */}
        <IntakeSection
          num={6}
          active={activeStage === 'memory'}
          label={t('intake.sections.memory_label', 'Discovery · Phase Six')}
          title={t('intake.sections.memory_title', 'Historical Data, Memory & Retention')}
          subtitle={t(
            'intake.sections.memory_sub',
            'What the system remembers, for how long, and who can see it later.',
          )}
        >
          <div id="section-memory" />

          <IntakeField
            num="6.01"
            label={t('intake.fields.history_retention', 'Conversation history retention')}
            help={t('intake.help.history_retention', 'How long full prompt/response transcripts are kept.')}
          >
            <RadioOptions
              name="history_retention"
              value={body.history_retention}
              onChange={(v) => set('history_retention', v)}
              options={[
                { value: 'none',       label: t('intake.opts.history_retention.none.label', 'None — ephemeral only') },
                { value: '30d',        label: t('intake.opts.history_retention.30d.label', '30 days') },
                { value: '90d',        label: t('intake.opts.history_retention.90d.label', '90 days') },
                { value: '1y',         label: t('intake.opts.history_retention.1y.label', '1 year') },
                { value: '7y',         label: t('intake.opts.history_retention.7y.label', '7+ years (regulated)') },
                { value: 'indefinite', label: t('intake.opts.history_retention.indefinite.label', 'Indefinite') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="6.02"
            label={t('intake.fields.user_memory', 'User memory / personalization')}
            help={t(
              'intake.help.user_memory',
              'Should the system learn about each user across sessions, or treat every interaction as fresh?',
            )}
          >
            <RadioOptions
              name="user_memory"
              value={body.user_memory}
              onChange={(v) => set('user_memory', v)}
              options={[
                { value: 'none',      label: t('intake.opts.user_memory.none.label', 'No memory') },
                { value: 'explicit',  label: t('intake.opts.user_memory.explicit.label', 'User-controlled'),
                                      desc:  t('intake.opts.user_memory.explicit.desc',  'Save preferences explicitly') },
                { value: 'automatic', label: t('intake.opts.user_memory.automatic.label', 'Automatic learning') },
                { value: 'hybrid',    label: t('intake.opts.user_memory.hybrid.label', 'Hybrid with transparency') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="6.03"
            label={t('intake.fields.audit_trail', 'Audit trail for AI decisions')}
            help={t(
              'intake.help.audit_trail',
              'For regulated environments, every AI decision may need to be reconstructable years later: input, prompt version, retrieved context, raw model output, post-processing, final user-facing output.',
            )}
            tags={['critical']}
          >
            <Textarea data-key="audit_trail"
              rows={3}
              value={body.audit_trail ?? ''}
              placeholder={t(
                'intake.ph.audit_trail',
                'Every AI-generated recommendation logs: input data snapshot, model version, prompt template version, retrieved context, raw model output, post-processing applied, final output shown to user, user action taken. Retained 7 years.',
              )}
              onChange={(e) => set('audit_trail', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="6.04"
            label={t('intake.fields.deletion_rights', 'Right to deletion / right to be forgotten')}
            help={t(
              'intake.help.deletion_rights',
              'GDPR, LGPD, and similar regimes require deletion of personal data on request — including from prompts, embeddings, and logs.',
            )}
          >
            <RadioOptions
              name="deletion_rights"
              value={body.deletion_rights}
              onChange={(v) => set('deletion_rights', v)}
              options={[
                { value: 'full',         label: t('intake.opts.deletion_rights.full.label', 'Full deletion within 30 days') },
                { value: 'anonymize',    label: t('intake.opts.deletion_rights.anonymize.label', 'Anonymize, retain aggregates') },
                { value: 'not_required', label: t('intake.opts.deletion_rights.not_required.label', 'Not required (no PII)') },
                { value: 'undefined',    label: t('intake.opts.deletion_rights.undefined.label', 'Not yet defined') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="6.05"
            label={t('intake.fields.backup_dr', 'Backup, archive & disaster recovery')}
            help={t('intake.help.backup_dr', 'RPO (recovery point objective) and RTO (recovery time objective).')}
          >
            <Input data-key="backup_dr"
              value={body.backup_dr ?? ''}
              placeholder={t('intake.ph.backup_dr', 'RPO: 1 hour · RTO: 4 hours · Geo-redundant backups, quarterly DR drills')}
              onChange={(e) => set('backup_dr', e.target.value)}
            />
          </IntakeField>

          <Nav stage="memory" nextKey="integrations" nextLabel={t('intake.next.integrations', 'Integrations')} />
        </IntakeSection>

        {/* ── 07. Integrations, Imports & Exports ──────────────────── */}
        <IntakeSection
          num={7}
          active={activeStage === 'integrations'}
          label={t('intake.sections.integrations_label', 'Discovery · Phase Seven')}
          title={t('intake.sections.integrations_title', 'Integrations, Imports & Exports')}
          subtitle={t(
            'intake.sections.integrations_sub',
            'The plumbing. AI applications live or die on how cleanly they connect to existing systems.',
          )}
        >
          <div id="section-integrations" />

          <IntakeField
            num="7.01"
            label={t('intake.fields.inbound', 'Inbound integrations')}
            help={t(
              'intake.help.inbound',
              'Each row: system, protocol, frequency, owner. Systems the AI app must read from.',
            )}
            required
            tags={['new']}
          >
            <DynList
              name="inbound"
              rows={body.inbound ?? []}
              cols={[
                { key: 'system',    placeholder: t('intake.ph.inbound.system',    'System') },
                { key: 'protocol',  placeholder: t('intake.ph.inbound.protocol',  'Protocol') },
                { key: 'frequency', placeholder: t('intake.ph.inbound.frequency', 'Frequency') },
                { key: 'owner',     placeholder: t('intake.ph.inbound.owner',     'Owner') },
              ]}
              addLabel={t('intake.dyn.add_inbound', '+ Add Inbound Integration')}
              onChange={(rows) => set('inbound', rows)}
            />
          </IntakeField>

          <IntakeField
            num="7.02"
            label={t('intake.fields.outbound', 'Outbound integrations')}
            help={t(
              'intake.help.outbound',
              'Each row: system, action triggered, format, owner. Systems the AI app writes to or triggers actions in.',
            )}
            tags={['new']}
          >
            <DynList
              name="outbound"
              rows={body.outbound ?? []}
              cols={[
                { key: 'system', placeholder: t('intake.ph.outbound.system', 'Target system') },
                { key: 'action', placeholder: t('intake.ph.outbound.action', 'Action') },
                { key: 'format', placeholder: t('intake.ph.outbound.format', 'Format') },
                { key: 'owner',  placeholder: t('intake.ph.outbound.owner',  'Owner') },
              ]}
              addLabel={t('intake.dyn.add_outbound', '+ Add Outbound Integration')}
              onChange={(rows) => set('outbound', rows)}
            />
          </IntakeField>

          <IntakeField
            num="7.03"
            label={t('intake.fields.imports', 'Bulk import requirements')}
            help={t('intake.help.imports', 'Formats, volumes, and frequency for one-time or periodic data loads.')}
          >
            <Textarea data-key="imports"
              rows={3}
              value={body.imports ?? ''}
              placeholder={t(
                'intake.ph.imports',
                'Initial historical load: 500K records via CSV; ongoing: monthly XLSX uploads from regional offices; ad-hoc: PDF document drops up to 200MB...',
              )}
              onChange={(e) => set('imports', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="7.04"
            label={t('intake.fields.exports', 'Export requirements')}
            help={t(
              'intake.help.exports',
              'What users and downstream systems need to extract. Formats, scheduling, access control.',
            )}
          >
            <Textarea data-key="exports"
              rows={3}
              value={body.exports ?? ''}
              placeholder={t(
                'intake.ph.exports',
                'User-initiated: PDF report, Excel of underlying data · Scheduled: daily JSON feed to BI tool · Compliance: full audit log export, monthly CSV',
              )}
              onChange={(e) => set('exports', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="7.05"
            label={t('intake.fields.api_exposure', 'API exposure')}
            help={t('intake.help.api_exposure', 'Will this system expose APIs for others to consume?')}
          >
            <RadioOptions
              name="api_exposure"
              value={body.api_exposure}
              onChange={(v) => set('api_exposure', v)}
              options={[
                { value: 'none',     label: t('intake.opts.api_exposure.none.label', 'No public API') },
                { value: 'internal', label: t('intake.opts.api_exposure.internal.label', 'Internal only') },
                { value: 'partner',  label: t('intake.opts.api_exposure.partner.label', 'Partner-facing') },
                { value: 'public',   label: t('intake.opts.api_exposure.public.label', 'Public API') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="7.06"
            label={t('intake.fields.webhooks', 'Webhooks & event streaming')}
            help={t('intake.help.webhooks', 'Real-time push of AI events to external systems.')}
          >
            <Textarea data-key="webhooks"
              rows={2}
              value={body.webhooks ?? ''}
              placeholder={t(
                'intake.ph.webhooks',
                'Webhook on completion, retry policy, dead-letter queue, Kafka stream for high-volume events...',
              )}
              onChange={(e) => set('webhooks', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="7.07"
            label={t('intake.fields.integration_errors', 'Integration error handling')}
            help={t(
              'intake.help.integration_errors',
              'How should integration failures be handled? Retry strategy, fallback, rollback, notification.',
            )}
            tags={['new']}
          >
            <Textarea data-key="integration_errors"
              rows={3}
              value={body.integration_errors ?? ''}
              placeholder={t(
                'intake.ph.integration_errors',
                'Retry 3 times with exponential backoff (1s, 4s, 16s) · Circuit breaker after 5 consecutive failures · Fallback to cached value where possible · DLQ for failed events · PagerDuty alert on >2% error rate over 5 min...',
              )}
              onChange={(e) => set('integration_errors', e.target.value)}
            />
          </IntakeField>

          <Nav stage="integrations" nextKey="scalability" nextLabel={t('intake.next.scalability', 'Scalability')} />
        </IntakeSection>

        {/* ── 08. Scalability, Performance & Cost ──────────────────── */}
        <IntakeSection
          num={8}
          active={activeStage === 'scalability'}
          label={t('intake.sections.scalability_label', 'Discovery · Phase Eight')}
          title={t('intake.sections.scalability_title', 'Scalability, Performance & Cost')}
          subtitle={t(
            'intake.sections.scalability_sub',
            'The economics of AI at scale. Token costs are a recurring P&L line, not a one-time bill.',
          )}
        >
          <div id="section-scalability" />

          <IntakeField
            num="8.01"
            label={t('intake.fields.volume', 'Expected request volume')}
            help={t(
              'intake.help.volume',
              'Per day at launch and at year 1. Distinguish interactive (user-initiated) from batch.',
            )}
            required
          >
            <Input data-key="volume"
              value={body.volume ?? ''}
              placeholder={t(
                'intake.ph.volume',
                'Launch: 500 interactive/day, 5K batch/day · Year 1: 5K interactive, 50K batch',
              )}
              onChange={(e) => set('volume', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="8.02"
            label={t('intake.fields.concurrency', 'Peak concurrency')}
            help={t('intake.help.concurrency', 'Simultaneous users at the busiest moment. Drives infrastructure sizing.')}
          >
            <Input data-key="concurrency"
              value={body.concurrency ?? ''}
              placeholder={t('intake.ph.concurrency', '50 concurrent users at peak (9am ET regional opens)')}
              onChange={(e) => set('concurrency', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="8.03"
            label={t('intake.fields.latency', 'Latency budget')}
            help={t(
              'intake.help.latency',
              'User-perceived response time tolerance. AI inference is slower than traditional apps; expectations must be set early.',
            )}
          >
            <RadioOptions
              name="latency"
              value={body.latency}
              onChange={(v) => set('latency', v)}
              options={[
                { value: 'under_1s', label: t('intake.opts.latency.under_1s.label', 'Under 1s — interactive') },
                { value: '1_5s',     label: t('intake.opts.latency.1_5s.label', '1–5s — conversational') },
                { value: '5_30s',    label: t('intake.opts.latency.5_30s.label', '5–30s — analysis OK') },
                { value: 'async',    label: t('intake.opts.latency.async.label', 'Asynchronous OK'),
                                     desc:  t('intake.opts.latency.async.desc',  'Notify when ready') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="8.04"
            label={t('intake.fields.streaming', 'Streaming responses?')}
            help={t(
              'intake.help.streaming',
              'Token-by-token streaming dramatically improves perceived latency. Not all integrations support it.',
            )}
          >
            <RadioOptions
              name="streaming"
              value={body.streaming}
              onChange={(v) => set('streaming', v)}
              options={[
                { value: 'yes',      label: t('intake.opts.streaming.yes.label', 'Yes — required') },
                { value: 'optional', label: t('intake.opts.streaming.optional.label', 'Optional') },
                { value: 'no',       label: t('intake.opts.streaming.no.label', 'No — full responses only') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="8.05"
            label={t('intake.fields.cost_ceiling', 'Token / inference cost ceiling')}
            help={t(
              'intake.help.cost_ceiling',
              'Per-user, per-month or per-transaction limit. Forgetting this is how enterprise AI bills surprise CFOs.',
            )}
            tags={['strategic']}
          >
            <Input data-key="cost_ceiling"
              value={body.cost_ceiling ?? ''}
              placeholder={t(
                'intake.ph.cost_ceiling',
                '< $0.50 per interaction, < $20/user/month, alerts at 80% of monthly budget',
              )}
              onChange={(e) => set('cost_ceiling', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="8.06"
            label={t('intake.fields.caching', 'Caching strategy')}
            help={t('intake.help.caching', 'Semantic cache, prompt cache, embedding cache — each cuts cost differently.')}
          >
            <CheckboxOptions
              name="caching"
              values={body.caching ?? []}
              onChange={(v) => set('caching', v)}
              options={[
                { value: 'prompt',    label: t('intake.opts.caching.prompt.label', 'Prompt caching') },
                { value: 'semantic',  label: t('intake.opts.caching.semantic.label', 'Semantic cache') },
                { value: 'embedding', label: t('intake.opts.caching.embedding.label', 'Embedding cache') },
                { value: 'cdn',       label: t('intake.opts.caching.cdn.label', 'CDN for static UI') },
                { value: 'none',      label: t('intake.opts.caching.none.label', 'None at launch') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="8.07"
            label={t('intake.fields.architecture', 'Architecture & scaling pattern')}
            help={t(
              'intake.help.architecture',
              'High-level shape of the system. Drives team structure, deployment complexity, and cost profile.',
            )}
            tags={['new']}
          >
            <RadioOptions
              name="architecture"
              value={body.architecture}
              onChange={(v) => set('architecture', v)}
              options={[
                { value: 'monolith',      label: t('intake.opts.architecture.monolith.label', 'Monolithic'),
                                          desc:  t('intake.opts.architecture.monolith.desc',  'Single deployable, simpler ops') },
                { value: 'microservices', label: t('intake.opts.architecture.microservices.label', 'Microservices'),
                                          desc:  t('intake.opts.architecture.microservices.desc',  'Independent scaling, more complex') },
                { value: 'serverless',    label: t('intake.opts.architecture.serverless.label', 'Serverless / functions'),
                                          desc:  t('intake.opts.architecture.serverless.desc',  'Lambda, Cloud Functions') },
                { value: 'hybrid',        label: t('intake.opts.architecture.hybrid.label', 'Hybrid'),
                                          desc:  t('intake.opts.architecture.hybrid.desc',  'Modular monolith + serverless workers') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="8.08"
            label={t('intake.fields.geo', 'Geographic distribution')}
            help={t(
              'intake.help.geo',
              'Where users access from. Drives latency, residency, and provider region selection — particularly for multi-country LATAM deployments.',
            )}
          >
            <Textarea data-key="geo"
              rows={2}
              value={body.geo ?? ''}
              placeholder={t(
                'intake.ph.geo',
                'Primary: Colombia, Mexico, Brazil. Secondary: rest of LATAM. Latency target degrades gracefully outside primary regions.',
              )}
              onChange={(e) => set('geo', e.target.value)}
            />
          </IntakeField>

          <Nav stage="scalability" nextKey="governance" nextLabel={t('intake.next.governance', 'Governance')} />
        </IntakeSection>

        {/* ── 09. Governance, Compliance & Risk ────────────────────── */}
        <IntakeSection
          num={9}
          active={activeStage === 'governance'}
          label={t('intake.sections.governance_label', 'Discovery · Phase Nine')}
          title={t('intake.sections.governance_title', 'Governance, Compliance & Risk')}
          subtitle={t(
            'intake.sections.governance_sub',
            'The questions auditors will ask in 18 months. Answering them now is dramatically cheaper.',
          )}
        >
          <div id="section-governance" />

          <IntakeField
            num="9.01"
            label={t('intake.fields.regulations', 'Applicable regulations')}
            help={t('intake.help.regulations', 'Every framework that touches the use case. Be exhaustive.')}
            tags={['critical']}
          >
            <CheckboxOptions
              name="regulations"
              values={body.regulations ?? []}
              onChange={(v) => set('regulations', v)}
              options={[
                { value: 'gdpr',     label: t('intake.opts.regulations.gdpr.label', 'GDPR (EU)') },
                { value: 'lgpd',     label: t('intake.opts.regulations.lgpd.label', 'LGPD (Brazil)') },
                { value: 'habeas',   label: t('intake.opts.regulations.habeas.label', 'Habeas Data (Colombia)') },
                { value: 'lfpdppp',  label: t('intake.opts.regulations.lfpdppp.label', 'LFPDPPP (Mexico)') },
                { value: 'hipaa',    label: t('intake.opts.regulations.hipaa.label', 'HIPAA (US health)') },
                { value: 'pci',      label: t('intake.opts.regulations.pci.label', 'PCI-DSS') },
                { value: 'sox',      label: t('intake.opts.regulations.sox.label', 'SOX') },
                { value: 'ccpa',     label: t('intake.opts.regulations.ccpa.label', 'CCPA (California)') },
                { value: 'soc2',     label: t('intake.opts.regulations.soc2.label', 'SOC 2') },
                { value: 'iso27001', label: t('intake.opts.regulations.iso27001.label', 'ISO 27001') },
                { value: 'euai',     label: t('intake.opts.regulations.euai.label', 'EU AI Act') },
                { value: 'banking',  label: t('intake.opts.regulations.banking.label', 'Local banking regulator') },
                { value: 'other',    label: t('intake.opts.regulations.other.label', 'Other (specify)') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="9.02"
            label={t('intake.fields.ai_risk', 'AI risk classification')}
            help={t('intake.help.ai_risk', 'Per EU AI Act framing, useful even outside Europe.')}
          >
            <RadioOptions
              name="ai_risk"
              value={body.ai_risk}
              onChange={(v) => set('ai_risk', v)}
              options={[
                { value: 'minimal',      label: t('intake.opts.ai_risk.minimal.label', 'Minimal — productivity tools') },
                { value: 'limited',      label: t('intake.opts.ai_risk.limited.label', 'Limited — chatbots, content') },
                { value: 'high',         label: t('intake.opts.ai_risk.high.label', 'High — credit, hiring, healthcare') },
                { value: 'unacceptable', label: t('intake.opts.ai_risk.unacceptable.label', 'Unacceptable — should not build') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="9.03"
            label={t('intake.fields.hitl', 'Human-in-the-loop requirements')}
            help={t('intake.help.hitl', 'Where human review is mandatory before AI output reaches end recipient.')}
          >
            <Textarea data-key="hitl"
              rows={3}
              value={body.hitl ?? ''}
              placeholder={t(
                'intake.ph.hitl',
                'All adverse decisions require senior underwriter approval. Automated approvals only for cases scored ≥ 0.95 confidence and below $50K exposure.',
              )}
              onChange={(e) => set('hitl', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="9.04"
            label={t('intake.fields.explainability', 'Explainability requirements')}
            help={t('intake.help.explainability', 'When the AI says "no," can someone explain why?')}
          >
            <RadioOptions
              name="explainability"
              value={body.explainability}
              onChange={(v) => set('explainability', v)}
              options={[
                { value: 'full',         label: t('intake.opts.explainability.full.label', 'Full — every decision') },
                { value: 'adverse',      label: t('intake.opts.explainability.adverse.label', 'For adverse outcomes only') },
                { value: 'aggregate',    label: t('intake.opts.explainability.aggregate.label', 'Aggregate / model-level') },
                { value: 'not_required', label: t('intake.opts.explainability.not_required.label', 'Not required') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="9.05"
            label={t('intake.fields.consent', 'User consent & disclosure')}
            help={t(
              'intake.help.consent',
              'How users are informed about AI involvement and consent to data processing.',
            )}
          >
            <Textarea data-key="consent"
              rows={3}
              value={body.consent ?? ''}
              placeholder={t(
                'intake.ph.consent',
                "First-use modal explaining AI capabilities and limits · Persistent 'AI-generated' badge on outputs · Cookie consent for telemetry · Right to opt out of personalization...",
              )}
              onChange={(e) => set('consent', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="9.06"
            label={t('intake.fields.vendor_gov', 'Vendor & model governance')}
            help={t(
              'intake.help.vendor_gov',
              'Approval process for new AI providers, model versions, and prompt changes in production.',
            )}
          >
            <Textarea data-key="vendor_gov"
              rows={2}
              value={body.vendor_gov ?? ''}
              placeholder={t(
                'intake.ph.vendor_gov',
                'AI Steering Committee reviews new providers. Model version pinning required; upgrades require regression eval and sign-off.',
              )}
              onChange={(e) => set('vendor_gov', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="9.07"
            label={t('intake.fields.risks', 'Risk register')}
            help={t(
              'intake.help.risks',
              'Each row: risk description, likelihood, impact, mitigation, owner. Honest naming halves the risk.',
            )}
            tags={['new', 'strategic']}
          >
            <DynList
              name="risks"
              rows={body.risks ?? []}
              cols={[
                { key: 'description', placeholder: t('intake.ph.risks.description', 'Risk') },
                { key: 'likelihood',  placeholder: t('intake.ph.risks.likelihood',  'Likelihood (Low/Med/High)') },
                { key: 'impact',      placeholder: t('intake.ph.risks.impact',      'Impact (Low/Med/High/Critical)') },
                { key: 'mitigation',  placeholder: t('intake.ph.risks.mitigation',  'Mitigation / Owner') },
              ]}
              addLabel={t('intake.dyn.add_risk', '+ Add Risk')}
              onChange={(rows) => set('risks', rows)}
            />
          </IntakeField>

          <Nav stage="governance" nextKey="operations" nextLabel={t('intake.next.operations', 'Operations')} />
        </IntakeSection>

        {/* ── 10. Operations, Observability & Support ──────────────── */}
        <IntakeSection
          num={10}
          active={activeStage === 'operations'}
          label={t('intake.sections.operations_label', 'Discovery · Phase Ten')}
          title={t('intake.sections.operations_title', 'Operations, Observability & Support')}
          subtitle={t(
            'intake.sections.operations_sub',
            'Day 2 reality. Most projects under-invest here and pay for it within 90 days of launch.',
          )}
        >
          <div id="section-operations" />

          <IntakeField
            num="10.01"
            label={t('intake.fields.slo', 'Service level objectives (SLOs)')}
            help={t('intake.help.slo', 'Availability, latency p95/p99, error rate.')}
          >
            <Input data-key="slo"
              value={body.slo ?? ''}
              placeholder={t('intake.ph.slo', '99.5% availability, p95 latency < 8s, error rate < 1%')}
              onChange={(e) => set('slo', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="10.02"
            label={t('intake.fields.deployment', 'Deployment model')}
            help={t('intake.help.deployment', 'Where the system runs, including AI inference.')}
            tags={['new']}
          >
            <RadioOptions
              name="deployment"
              value={body.deployment}
              onChange={(v) => set('deployment', v)}
              options={[
                { value: 'cloud_managed', label: t('intake.opts.deployment.cloud_managed.label', 'Cloud — managed services'),
                                          desc:  t('intake.opts.deployment.cloud_managed.desc',  'AWS, GCP, Azure') },
                { value: 'cloud_k8s',     label: t('intake.opts.deployment.cloud_k8s.label', 'Cloud — Kubernetes') },
                { value: 'onprem',        label: t('intake.opts.deployment.onprem.label', 'On-premise') },
                { value: 'hybrid',        label: t('intake.opts.deployment.hybrid.label', 'Hybrid') },
                { value: 'airgap',        label: t('intake.opts.deployment.airgap.label', 'Air-gapped'),
                                          desc:  t('intake.opts.deployment.airgap.desc',  'No external connectivity') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="10.03"
            label={t('intake.fields.observability', 'Observability stack')}
            help={t(
              'intake.help.observability',
              'Logging, metrics, tracing, AI-specific telemetry (token usage, hallucination flags, eval scores).',
            )}
          >
            <Textarea data-key="observability"
              rows={3}
              value={body.observability ?? ''}
              placeholder={t(
                'intake.ph.observability',
                'Datadog APM + logs, OpenTelemetry tracing, custom dashboard for token spend by tenant, prompt-version tagging, user-feedback signal pipeline...',
              )}
              onChange={(e) => set('observability', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="10.04"
            label={t('intake.fields.alerting', 'Monitoring & alerting')}
            help={t(
              'intake.help.alerting',
              'What gets paged, who gets paged, on what conditions. AI systems need new alert types beyond traditional infra.',
            )}
            tags={['new']}
          >
            <Textarea data-key="alerting"
              rows={3}
              value={body.alerting ?? ''}
              placeholder={t(
                'intake.ph.alerting',
                'P1: hallucination rate > 2x baseline, model provider down · P2: token cost > 120% forecast, p99 latency > 30s · P3: user thumbs-down rate > 15% over 1h · Alerts route to on-call via PagerDuty',
              )}
              onChange={(e) => set('alerting', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="10.05"
            label={t('intake.fields.devops', 'CI/CD & DevOps tooling')}
            help={t('intake.help.devops', 'How code, prompts, and model configurations are tested and deployed.')}
            tags={['new']}
          >
            <Textarea data-key="devops"
              rows={3}
              value={body.devops ?? ''}
              placeholder={t(
                'intake.ph.devops',
                'GitHub Actions for CI/CD · Terraform for IaC · Prompt versioning in Git with regression tests · Automated eval gate on merge to main · Blue-green deployment to production',
              )}
              onChange={(e) => set('devops', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="10.06"
            label={t('intake.fields.support', 'Support model')}
            help={t(
              'intake.help.support',
              'Who handles tier-1, tier-2, tier-3 issues. AI issues often require new triage skills.',
            )}
          >
            <Textarea data-key="support"
              rows={3}
              value={body.support ?? ''}
              placeholder={t(
                'intake.ph.support',
                'L1: helpdesk handles access issues · L2: AI ops team handles output quality, model errors · L3: data science + engineering for incidents and model issues',
              )}
              onChange={(e) => set('support', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="10.07"
            label={t('intake.fields.feedback', 'User feedback mechanism')}
            help={t(
              'intake.help.feedback',
              'In-product way for users to flag bad outputs. Fuel for continuous improvement.',
            )}
          >
            <Textarea data-key="feedback"
              rows={2}
              value={body.feedback ?? ''}
              placeholder={t(
                'intake.ph.feedback',
                'Thumbs up/down on every response · Required comment on negative · Weekly review by AI ops team',
              )}
              onChange={(e) => set('feedback', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="10.08"
            label={t('intake.fields.change_mgmt', 'Change management & rollout')}
            help={t(
              'intake.help.change_mgmt',
              'Strategy for deploying new prompts, model versions, and features without breaking trust.',
            )}
            tags={['new']}
          >
            <Textarea data-key="change_mgmt"
              rows={3}
              value={body.change_mgmt ?? ''}
              placeholder={t(
                'intake.ph.change_mgmt',
                'Canary deploys to 5% → 25% → 100% · A/B testing on prompt variations · Shadow mode for new model versions for 2 weeks before cutover · End-user training via in-app tour and recorded sessions',
              )}
              onChange={(e) => set('change_mgmt', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="10.09"
            label={t('intake.fields.incident', 'Incident response for AI-specific failures')}
            help={t(
              'intake.help.incident',
              'What happens when the model goes off the rails — degrades, hallucinates badly, gets jailbroken.',
            )}
            tags={['critical']}
          >
            <Textarea data-key="incident"
              rows={3}
              value={body.incident ?? ''}
              placeholder={t(
                'intake.ph.incident',
                'Kill switch to bypass AI and route to legacy flow · Automated rollback to last-good prompt version · PagerDuty alert on hallucination rate > 2x baseline · Mandatory post-mortem within 5 business days',
              )}
              onChange={(e) => set('incident', e.target.value)}
            />
          </IntakeField>

          <Nav stage="operations" nextKey="ux" nextLabel={t('intake.next.ux', 'UX & Trust')} />
        </IntakeSection>

        {/* ── 11. User Experience & Trust ──────────────────────────── */}
        <IntakeSection
          num={11}
          active={activeStage === 'ux'}
          label={t('intake.sections.ux_label', 'Discovery · Phase Eleven')}
          title={t('intake.sections.ux_title', 'User Experience & Trust')}
          subtitle={t(
            'intake.sections.ux_sub',
            'How the system behaves, communicates uncertainty, and earns the right to be relied upon.',
          )}
        >
          <div id="section-ux" />

          <IntakeField
            num="11.01"
            label={t('intake.fields.tone', 'Tone & persona')}
            help={t('intake.help.tone', 'How the AI presents itself. Formal, friendly, neutral, branded.')}
          >
            <Textarea data-key="tone"
              rows={2}
              value={body.tone ?? ''}
              placeholder={t(
                'intake.ph.tone',
                "Professional, concise, second-person. Avoids jargon. Never claims to be human. Branded as 'Aura' with a defined voice.",
              )}
              onChange={(e) => set('tone', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="11.02"
            label={t('intake.fields.citations', 'Citation & source transparency')}
            help={t(
              'intake.help.citations',
              'When the AI surfaces information, how does the user verify the source?',
            )}
          >
            <RadioOptions
              name="citations"
              value={body.citations}
              onChange={(v) => set('citations', v)}
              options={[
                { value: 'inline',    label: t('intake.opts.citations.inline.label', 'Inline citations to source') },
                { value: 'appendix',  label: t('intake.opts.citations.appendix.label', 'Sources in appendix') },
                { value: 'on_demand', label: t('intake.opts.citations.on_demand.label', 'On-demand "show sources"') },
                { value: 'none',      label: t('intake.opts.citations.none.label', 'Not applicable') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="11.03"
            label={t('intake.fields.confidence', 'Confidence communication')}
            help={t('intake.help.confidence', "How the system signals when it's uncertain.")}
          >
            <RadioOptions
              name="confidence"
              value={body.confidence}
              onChange={(v) => set('confidence', v)}
              options={[
                { value: 'numeric',     label: t('intake.opts.confidence.numeric.label', 'Numeric confidence score') },
                { value: 'qualitative', label: t('intake.opts.confidence.qualitative.label', 'Qualitative ("high/med/low")') },
                { value: 'natural',     label: t('intake.opts.confidence.natural.label', 'Natural language hedging') },
                { value: 'abstain',     label: t('intake.opts.confidence.abstain.label', 'Refuses if uncertain') },
              ]}
            />
          </IntakeField>

          <IntakeField
            num="11.04"
            label={t('intake.fields.disclosure', 'AI labeling & disclosure')}
            help={t(
              'intake.help.disclosure',
              "Required by emerging regulation in many jurisdictions. Always tell the user when they're interacting with AI.",
            )}
          >
            <Input data-key="disclosure"
              value={body.disclosure ?? ''}
              placeholder={t(
                'intake.ph.disclosure',
                "Persistent 'AI-generated' badge on outputs; first-use modal explaining capabilities and limits",
              )}
              onChange={(e) => set('disclosure', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="11.05"
            label={t('intake.fields.error_ux', 'Error messages & failure UX')}
            help={t(
              'intake.help.error_ux',
              "How the system communicates when it can't help. Often the difference between trusted and abandoned.",
            )}
            tags={['new']}
          >
            <Textarea data-key="error_ux"
              rows={2}
              value={body.error_ux ?? ''}
              placeholder={t(
                'intake.ph.error_ux',
                "Distinguishes 'I don't know' from 'system error.' Provides clear next steps. Routes complex cases to human reviewer with one click.",
              )}
              onChange={(e) => set('error_ux', e.target.value)}
            />
          </IntakeField>

          <Nav stage="ux" nextKey="delivery" nextLabel={t('intake.next.delivery', 'Timeline')} />
        </IntakeSection>

        {/* ── 12. Timeline, Team & Delivery ────────────────────────── */}
        <IntakeSection
          num={12}
          active={activeStage === 'delivery'}
          label={t('intake.sections.delivery_label', 'Discovery · Phase Twelve')}
          title={t('intake.sections.delivery_title', 'Timeline, Team & Delivery')}
          subtitle={t(
            'intake.sections.delivery_sub',
            "The closing act. What's known, what's unknown, what could derail it.",
          )}
        >
          <div id="section-delivery" />

          <IntakeField
            num="12.01"
            label={t('intake.fields.milestones', 'Target dates & milestones')}
            help={t(
              'intake.help.milestones',
              'Each row: milestone, target date, status (committed / aspirational / blocked).',
            )}
            tags={['new']}
          >
            <DynList
              name="milestones"
              rows={body.milestones ?? []}
              cols={[
                { key: 'milestone', placeholder: t('intake.ph.milestone.milestone', 'Milestone') },
                { key: 'date',      placeholder: t('intake.ph.milestone.date',      'Target date') },
                { key: 'status',    placeholder: t('intake.ph.milestone.status',    'Status (committed / aspirational / blocked)') },
              ]}
              addLabel={t('intake.dyn.add_milestone', '+ Add Milestone')}
              onChange={(rows) => set('milestones', rows)}
            />
          </IntakeField>

          <IntakeField
            num="12.02"
            label={t('intake.fields.team', 'Team composition & gaps')}
            help={t(
              'intake.help.team',
              'Roles assigned and gaps. AI builds need product, engineering, data, ML, security, legal, change management.',
            )}
          >
            <Textarea data-key="team"
              rows={4}
              value={body.team ?? ''}
              placeholder={t(
                'intake.ph.team',
                '✓ Product manager, tech lead, 2 engineers, data engineer\n✗ AI/ML engineer (gap), security architect (part-time), legal (limited bandwidth), change manager (none)',
              )}
              onChange={(e) => set('team', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="12.03"
            label={t('intake.fields.dependencies', 'Known dependencies')}
            help={t(
              'intake.help.dependencies',
              'Other teams, vendors, decisions, or events the project waits on.',
            )}
          >
            <Textarea data-key="dependencies"
              rows={3}
              value={body.dependencies ?? ''}
              placeholder={t(
                'intake.ph.dependencies',
                '- Snowflake migration (Data Eng) — must finish by Aug\n- Vendor security review of model provider — in flight\n- Steering committee approval of risk classification — Sept',
              )}
              onChange={(e) => set('dependencies', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="12.04"
            label={t('intake.fields.sla', 'SLA & support tier')}
            help={t('intake.help.sla', 'What the business commits to internal users or external customers.')}
            tags={['new']}
          >
            <Textarea data-key="sla"
              rows={2}
              value={body.sla ?? ''}
              placeholder={t(
                'intake.ph.sla',
                'Business hours support 7-19 local · 99.5% uptime committed · 4-hour P1 response, 1-business-day P2 · Quarterly business review with sponsor',
              )}
              onChange={(e) => set('sla', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="12.05"
            label={t('intake.fields.open_questions', 'Open questions for follow-up')}
            help={t(
              'intake.help.open_questions',
              'Anything this discovery surfaced that needs a separate working session.',
            )}
          >
            <Textarea data-key="open_questions"
              rows={3}
              value={body.open_questions ?? ''}
              placeholder={t(
                'intake.ph.open_questions',
                '- Detailed data residency review with regional legal counsel\n- Workshop on token cost modeling at scale\n- Architecture review with enterprise security',
              )}
              onChange={(e) => set('open_questions', e.target.value)}
            />
          </IntakeField>

          <IntakeField
            num="12.06"
            label={t('intake.fields.notes', 'Notes & additional context')}
            help={t(
              'intake.help.notes',
              'Anything else the engineering team should know going into this build.',
            )}
          >
            <Textarea data-key="notes"
              rows={3}
              value={body.notes ?? ''}
              placeholder={t(
                'intake.ph.notes',
                'Free text — context, history, political dynamics, prior attempts...',
              )}
              onChange={(e) => set('notes', e.target.value)}
            />
          </IntakeField>

          <Nav stage="delivery" nextKey="generate" nextLabel={t('intake.next.generate', 'Generate Deliverables')} />
        </IntakeSection>

        {/* ── 13. Generate ─────────────────────────────────────────── */}
        <IntakeSection
          num={13}
          active={activeStage === 'generate'}
          label={t('intake.sections.generate_label', 'Generate · Phase Final')}
          title={t('intake.sections.generate_title', 'Generate Deliverables')}
          subtitle={t(
            'intake.sections.generate_sub',
            'Lock the brief, hand off to the agent fleet, and receive 8 deliverables.',
          )}
        >
          <div id="section-generate" />
          {error && <p className="auth-card__error">{error}</p>}
          <div className="generate-footer">
            <button type="submit" className="dock-btn dock-btn--lg" disabled={saving}>
              {saving ? t('common.saving') : t('intake.save_draft')}
            </button>
          </div>
        </IntakeSection>
      </form>
      <PageFooter />
    </>
  )
}
